/**
 * Admin: Environments Page
 * List all lab environment definitions with edit/clone/delete actions.
 */

import { useState, useEffect, useCallback } from 'react';
import { useNavigate } from 'react-router-dom';
import { apiGet, apiPost, apiDelete, ApiError } from '../services/api';
import { Button } from '../components/ui/button';
import { Card, CardContent, CardHeader } from '../components/ui/card';
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
} from '../components/ui/alert-dialog';
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from '../components/ui/dialog';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import type { LabEnvironmentDefinition } from '@cloak-next/shared';

interface AdminEnvironment extends LabEnvironmentDefinition {
  createdAt?: string;
  updatedAt?: string;
}

export function EnvironmentsPage() {
  const navigate = useNavigate();
  const [environments, setEnvironments] = useState<AdminEnvironment[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [successMessage, setSuccessMessage] = useState('');

  // Delete dialog state
  const [deleteDialogOpen, setDeleteDialogOpen] = useState(false);
  const [envToDelete, setEnvToDelete] = useState<AdminEnvironment | null>(null);
  const [deleting, setDeleting] = useState(false);
  const [deleteError, setDeleteError] = useState<string | null>(null);

  // Clone dialog state
  const [cloneDialogOpen, setCloneDialogOpen] = useState(false);
  const [envToClone, setEnvToClone] = useState<AdminEnvironment | null>(null);
  const [cloneNewId, setCloneNewId] = useState('');
  const [cloneNewName, setCloneNewName] = useState('');
  const [cloning, setCloning] = useState(false);
  const [cloneError, setCloneError] = useState<string | null>(null);

  const fetchEnvironments = useCallback(async () => {
    try {
      setLoading(true);
      setError(null);
      const response = await apiGet<{ success: boolean; data: AdminEnvironment[] }>('/admin/environments');
      setEnvironments(response.data || []);
    } catch (err) {
      const message = err instanceof ApiError ? err.message : 'Failed to load environments';
      setError(message);
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    fetchEnvironments();
  }, [fetchEnvironments]);

  const handleDeleteClick = (env: AdminEnvironment) => {
    setEnvToDelete(env);
    setDeleteError(null);
    setDeleteDialogOpen(true);
  };

  const handleDeleteConfirm = async () => {
    if (!envToDelete) return;
    setDeleting(true);
    setDeleteError(null);
    try {
      await apiDelete(`/admin/environments/${encodeURIComponent(envToDelete.id)}`);
      setDeleteDialogOpen(false);
      setEnvToDelete(null);
      setSuccessMessage(`Environment "${envToDelete.name}" deleted`);
      await fetchEnvironments();
    } catch (err) {
      const message = err instanceof ApiError ? err.message : 'Delete failed';
      setDeleteError(message);
    } finally {
      setDeleting(false);
    }
  };

  const handleCloneClick = (env: AdminEnvironment) => {
    setEnvToClone(env);
    setCloneNewId(`${env.id}-copy`);
    setCloneNewName(`${env.name} (Copy)`);
    setCloneError(null);
    setCloneDialogOpen(true);
  };

  const handleCloneConfirm = async () => {
    if (!envToClone) return;
    if (!cloneNewId.trim() || !cloneNewName.trim()) {
      setCloneError('Both ID and name are required');
      return;
    }
    setCloning(true);
    setCloneError(null);
    try {
      await apiPost(`/admin/environments/${encodeURIComponent(envToClone.id)}/clone`, {
        newId: cloneNewId.trim(),
        newName: cloneNewName.trim(),
      });
      setCloneDialogOpen(false);
      setEnvToClone(null);
      setSuccessMessage(`Environment cloned as "${cloneNewName.trim()}"`);
      await fetchEnvironments();
    } catch (err) {
      const message = err instanceof ApiError ? err.message : 'Clone failed';
      setCloneError(message);
    } finally {
      setCloning(false);
    }
  };

  return (
    <div className="space-y-6">
      {/* Header */}
      <div className="flex justify-between items-center">
        <div>
          <h2 className="text-3xl font-bold">Lab Environments</h2>
          <p className="text-muted-foreground mt-1">
            Manage lab environment definitions. Changes take effect when new instances are primed.
          </p>
        </div>
        <Button onClick={() => navigate('/admin/environments/new')}>
          New Environment
        </Button>
      </div>

      {/* Success message */}
      {successMessage && (
        <div className="bg-green-50 border border-green-200 text-green-800 px-4 py-3 rounded">
          {successMessage}
        </div>
      )}

      {/* Error message */}
      {error && (
        <div className="bg-red-50 border border-red-200 text-red-800 px-4 py-3 rounded">
          {error}
        </div>
      )}

      {/* Loading */}
      {loading && (
        <div className="text-center py-12 text-muted-foreground">Loading environments...</div>
      )}

      {/* Environment cards */}
      {!loading && environments.length === 0 && !error && (
        <div className="text-center py-12 text-muted-foreground border rounded-lg">
          No environments defined. Create one to get started.
        </div>
      )}

      <div className="grid gap-4 md:grid-cols-2">
        {environments.map((env) => (
          <Card key={env.id}>
            <CardHeader>
              <div className="flex justify-between items-start">
                <div>
                  <h3 className="text-lg font-semibold">{env.name}</h3>
                  <span className="font-mono text-xs text-muted-foreground">{env.id}</span>
                </div>
                <span className="text-sm font-medium text-green-700">
                  ${env.estimatedCostPerHour.toFixed(4)}/hr
                </span>
              </div>
            </CardHeader>
            <CardContent className="space-y-3">
              <p className="text-sm text-muted-foreground">{env.description}</p>

              <div className="flex gap-4 text-sm">
                <span>
                  <span className="text-muted-foreground">Hosts:</span>{' '}
                  <span className="font-medium">{env.instances.length}</span>
                </span>
                {env.category && (
                  <span>
                    <span className="text-muted-foreground">Category:</span>{' '}
                    <span className="font-medium">{env.category}</span>
                  </span>
                )}
              </div>

              {/* Instance summary */}
              <div className="flex flex-wrap gap-1">
                {env.instances.map((inst) => (
                  <span
                    key={inst.id}
                    className="inline-flex items-center px-2 py-0.5 rounded text-xs font-medium bg-muted text-muted-foreground"
                  >
                    {inst.id} ({inst.instanceType})
                  </span>
                ))}
              </div>

              {env.updatedAt && (
                <p className="text-xs text-muted-foreground">
                  Updated {new Date(env.updatedAt).toLocaleDateString()}
                </p>
              )}

              <div className="flex gap-2 pt-1">
                <Button
                  size="sm"
                  variant="outline"
                  onClick={() => navigate(`/admin/environments/${encodeURIComponent(env.id)}/edit`)}
                >
                  Edit
                </Button>
                <Button
                  size="sm"
                  variant="outline"
                  onClick={() => handleCloneClick(env)}
                >
                  Clone
                </Button>
                <Button
                  size="sm"
                  variant="ghost"
                  className="text-destructive hover:text-destructive"
                  onClick={() => handleDeleteClick(env)}
                >
                  Delete
                </Button>
              </div>
            </CardContent>
          </Card>
        ))}
      </div>

      {/* Delete confirmation dialog */}
      <AlertDialog open={deleteDialogOpen} onOpenChange={setDeleteDialogOpen}>
        <AlertDialogContent>
          <AlertDialogHeader>
            <AlertDialogTitle>Delete Environment</AlertDialogTitle>
            <AlertDialogDescription>
              Delete "{envToDelete?.name}"? This cannot be undone. The environment cannot be
              deleted if any class currently references it.
            </AlertDialogDescription>
          </AlertDialogHeader>
          {deleteError && (
            <div className="bg-red-50 border border-red-200 text-red-800 px-3 py-2 rounded text-sm">
              {deleteError}
            </div>
          )}
          <AlertDialogFooter>
            <AlertDialogCancel disabled={deleting}>Cancel</AlertDialogCancel>
            <AlertDialogAction
              onClick={handleDeleteConfirm}
              disabled={deleting}
              className="bg-destructive text-destructive-foreground hover:bg-destructive/90"
            >
              {deleting ? 'Deleting...' : 'Delete'}
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>

      {/* Clone dialog */}
      <Dialog open={cloneDialogOpen} onOpenChange={setCloneDialogOpen}>
        <DialogContent>
          <DialogHeader>
            <DialogTitle>Clone Environment</DialogTitle>
            <DialogDescription>
              Create a copy of "{envToClone?.name}" with a new ID and name.
            </DialogDescription>
          </DialogHeader>
          <div className="space-y-4 py-2">
            <div className="space-y-1">
              <Label htmlFor="clone-id">New ID (slug)</Label>
              <Input
                id="clone-id"
                value={cloneNewId}
                onChange={(e) => setCloneNewId(e.target.value)}
                placeholder="e.g. web-apps-custom"
                className="font-mono"
              />
              <p className="text-xs text-muted-foreground">
                Lowercase letters, numbers, and hyphens only.
              </p>
            </div>
            <div className="space-y-1">
              <Label htmlFor="clone-name">New Name</Label>
              <Input
                id="clone-name"
                value={cloneNewName}
                onChange={(e) => setCloneNewName(e.target.value)}
                placeholder="e.g. Web Applications (Custom)"
              />
            </div>
            {cloneError && (
              <div className="bg-red-50 border border-red-200 text-red-800 px-3 py-2 rounded text-sm">
                {cloneError}
              </div>
            )}
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setCloneDialogOpen(false)} disabled={cloning}>
              Cancel
            </Button>
            <Button onClick={handleCloneConfirm} disabled={cloning}>
              {cloning ? 'Cloning...' : 'Clone'}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}
